import { analytics, externalMeta, isDelegate, isProduction, observe, plugins, userProjects } from '@dabble/app';
import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';

interface GEvent {
  event_category?: string;
  event_label?: string;
  value?: number;
  send_to?: string;
  [key: string]: any;
}
interface GParams {
  [key: string]: any;
}
interface GTag {
  (type: 'event', action: string, event?: GEvent): void;
  (type: 'config', measurementId: string, params?: GParams): void;
  (type: 'set', params?: GParams): void;
  (type: 'set', target: string, params?: GParams): void;
}

declare global {
  interface Window {
    'ga-disable-analyticsId': boolean;
    fbq: (type: 'track' | 'init', name: string, value?: any) => void;
    gtag: GTag;
    Intercom: (type: 'update' | 'show', value?: any) => void;
    profitwell: (property: string, value: string) => void;
    _cio: {
      identify: (value?: any) => void;
    };
  }
}

if (!isProduction || isDelegate) {
  window['ga-disable-analyticsId'] = true;
} else {
  // CHECKOUT
  window.addEventListener('checkout', ({ detail }: CustomEvent) => {
    let { type, plan, coupon, total, lifetime, priceDiff, invoiceNumber } = detail;
    const value = getCheckoutValue(plan, coupon);
    if (lifetime) type = 'upgrade';

    // Store comprehensive checkout data for server-side processing
    recordWithTracking('purchase', {
      type,
      plan_name: plan.nickname,
      plan_id: plan.id,
      currency: plan.currency.toUpperCase(),
      total,
      value,
      coupon_id: coupon?.id,
      price_diff: priceDiff,
      lifetime,
    });

    // Client-side conversion tracking for new purchases
    if (type === 'new') {
      // Facebook Subscribe tracking
      window.fbq &&
        window.fbq('track', 'Subscribe', {
          value: value,
          currency: plan.currency.toUpperCase(),
          content_ids: [plan.id],
        });

      // Google Analytics Subscribe tracking
      if (window.gtag) {
        window.gtag('event', 'subscribe', {
          transaction_id: invoiceNumber,
          value: value,
          currency: plan.currency.toUpperCase(),
          coupon: coupon?.id,
          items: [
            {
              item_id: plan.id,
              item_name: plan.nickname,
              price: value,
              quantity: 1,
            },
          ],
        });
        window.gtag('event', 'conversion', {
          send_to: 'AW-831705431/AAvSCKSKou0bENeiy4wD',
          value: value,
          currency: plan.currency.toUpperCase(),
        });
      }
    } else if (window.fbq && (type === 'upgrade' || type === 'downgrade')) {
      window.fbq('track', type === 'upgrade' ? 'Upgrade' : 'Downgrade', {
        value: value,
        currency: plan.currency.toUpperCase(),
        content_ids: [plan.id],
      });
    }
  });

  // BEGIN CHECKOUT
  window.addEventListener('begincheckout', ({ detail: { plan, coupon, total } }: CustomEvent) => {
    const value = total || getCheckoutValue(plan, coupon);
    recordWithTracking('checkout_start', {
      coupon_id: coupon?.id,
      currency: plan.currency,
      plan_id: plan.id,
      value,
    });

    // Google Analytics begin_checkout event
    window.gtag &&
      window.gtag('event', 'begin_checkout', {
        currency: plan.currency.toUpperCase(),
        value,
        coupon: coupon?.id,
        items: [
          {
            item_id: plan.id,
            item_name: plan.nickname,
            price: value,
            quantity: 1,
          },
        ],
      });

    // Facebook InitiateCheckout event
    window.fbq &&
      window.fbq('track', 'InitiateCheckout', {
        currency: plan.currency.toUpperCase(),
        value,
        content_ids: [plan.id],
      });
  });

  // NANOWORDCOUNT
  window.addEventListener('nanowordcount', ({ detail: { wordcount } }: CustomEvent) => {
    const nanowrimo = `nanowrimo.${format(new Date(), 'yyyy-MM')}`;
    analytics.recordUserData({ [nanowrimo]: wordcount });
  });

  let lastPage = '';
  let pinterestLoaded = false;

  // PAGE CHANGE
  window.addEventListener('currentscreen', ({ detail: { page_title, content_group } }: CustomEvent) => {
    const page_referrer = lastPage || document.referrer;
    lastPage = location.href;
    const resolution = `${Math.round(window.screen.width * devicePixelRatio)}x${Math.round(
      window.screen.height * devicePixelRatio
    )}`;
    recordWithTracking('page_view', {
      page_location: location.href,
      page_referrer,
      page_title: page_title,
      content_group,
      ul: navigator.language,
      sr: resolution,
    });
    window.gtag && window.gtag('event', 'page_view', {
      page_referrer,
      page_title,
      content_group,
    });

    // Facebook PageView event
    window.fbq &&
      window.fbq('track', 'PageView', {
        content_name: page_title,
        content_category: 'page_view',
      });
  });

  // SIGN UP
  window.addEventListener('signup', () => {
    recordWithTracking('signup', { method: 'password' });

    // Google Analytics sign_up event
    if (window.gtag) {
      window.gtag('event', 'sign_up', {
        method: 'password',
        value: 38,
        currency: 'USD',
      });
      window.gtag('event', 'conversion', {
        send_to: 'AW-831705431/aoDJCM_5oO0bENeiy4wD',
      });
    }

    // Facebook CompleteRegistration event
    window.fbq &&
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'user_registration',
      });
  });

  // SIGN IN
  window.addEventListener('signin', ({ detail: { uid, email } }: CustomEvent) => {
    recordWithTracking('login', { method: 'password', uid, email });

    // Google Analytics login event
    window.gtag &&
      window.gtag('event', 'login', {
        method: 'password',
      });
  });

  // AUTHENTICATED
  window.addEventListener('authed', ({ detail: { uid, name, email } }: CustomEvent) => {
    // Record authentication event for server-side processing
    recordWithTracking('user_authenticated', { uid, name, email });

    // Keep direct identity calls for Google/Facebook user_id setting
    if (window.gtag) {
      window.gtag('set', { user_id: uid });
      if (email) {
        window.gtag('set', 'user_data', { email: normalizeEmail(email) });
      }
    }
    window.fbq && window.fbq('init', '349123249249445', { external_id: uid });

    // Keep third-party services that need direct client-side integration
    window._cio && window._cio.identify({ id: uid, email });
    window.profitwell && window.profitwell('user_email', email);
    Sentry.setUser({ id: uid });
  });

  // PROJECT CREATION
  window.addEventListener('projectcreated', () => {
    recordWithTracking('create_project', {
      event_category: 'projects',
      event_label: 'Project Creation',
    });
    analytics.recordUserData({ projectCount: userProjects.get().length });

    // Google Analytics created event for projects
    window.gtag &&
      window.gtag('event', 'created', {
        event_category: 'projects',
        event_label: 'Project Creation',
      });

    // Facebook CreateProject event
    window.fbq &&
      window.fbq('track', 'CreateProject', {
        content_type: 'project',
        content_name: 'project_creation',
      });
  });

  // DOCUMENT CREATION
  window.addEventListener('create-doc', (event: CustomEvent) => {
    const docType = event.detail.doc.type;
    recordWithTracking('create_document', {
      document_type: docType,
      event_category: 'documents',
      event_label: 'Document Creation',
    });

    // Google Analytics created event for documents
    window.gtag &&
      window.gtag('event', 'created', {
        event_category: 'documents',
        event_label: 'Document Creation',
        content_type: docType,
      });

    // Facebook CreateDocument event
    window.fbq &&
      window.fbq('track', 'CreateDocument', {
        content_type: docType || 'document',
        content_name: 'document_creation',
      });
  });

  // REWARD GOALS
  window.addEventListener('reached-reward-goal', () => {
    recordWithTracking('goal_achieved', {
      event_category: 'goals',
      event_label: 'Reward Goal Reached',
    });

    // Google Analytics unlock_achievement event
    window.gtag &&
      window.gtag('event', 'unlock_achievement', {
        achievement_id: 'reward_goal',
        event_category: 'goals',
      });
  });

  window.addEventListener('reward-applied', () => {
    recordWithTracking('reward_applied', {
      event_category: 'goals',
      event_label: 'Reward Applied',
    });
  });

  // UPDATE INTERCOM
  const unsubscribeInt = observe([externalMeta, plugins.stores.currentUser], ([meta, user]) => {
    if (meta?.intercomHash && user?.uid) {
      Promise.resolve().then(() => unsubscribeInt());
      if (window.Intercom) {
        const { uid, email, name } = user;
        window.Intercom('update', { user_id: uid, name, email, user_hash: meta.intercomHash });
      }
    }
  });
}

// Per Google's enhanced conversions spec: trim, lowercase, and strip dots/plus-tags from gmail.
function normalizeEmail(email: string): string {
  let normalized = email.trim().toLowerCase();
  const [local, domain] = normalized.split('@');
  if (!domain) return normalized;
  const localBase = local.split('+')[0];
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return `${localBase.replace(/\./g, '')}@${domain}`;
  }
  return `${localBase}@${domain}`;
}

function getCheckoutValue(plan: any, coupon: any): number {
  let value = plan.amount / 100;
  if (coupon && coupon.percent_off && (coupon.duration === 'forever' || coupon.duration_in_months === 12)) {
    value *= coupon.percent_off / 100;
  }
  return value;
}

/**
 * Parse cookies and extract tracking identifiers for Google Analytics and Facebook
 */
function getTrackingIdentifiers(forMeasurementId?: string): {
  google_client_id?: string;
  google_session_id?: string;
  facebook_fbp?: string;
  facebook_fbc?: string;
} {
  try {
    const cookies = Object.fromEntries(
      document.cookie.split('; ').map(row => {
        const parts = row.split('=');
        return [parts[0], parts.length > 1 ? decodeURIComponent(parts[1]) : ''];
      })
    );

    const trackingIds: any = {};

    // Extract Google Analytics client ID from _ga cookie
    if (cookies._ga) {
      try {
        const gaParts = cookies._ga.split('.');
        if (gaParts.length >= 4) {
          trackingIds.google_client_id = gaParts.slice(2, 4).join('.');
        }
      } catch (error) {
        console.warn('Error parsing Google Analytics client ID:', error);
      }
    }

    // Extract Google Analytics session ID from measurement-specific cookie (e.g., _ga_09N6VRHLQG)
    const gaCookiePattern = /^_ga_[A-Z0-9]+$/;
    for (const [cookieName, cookieValue] of Object.entries(cookies)) {
      if (
        gaCookiePattern.test(cookieName) &&
        cookieValue &&
        (!forMeasurementId || cookieName.includes(forMeasurementId))
      ) {
        try {
          const sessionParts = cookieValue.split(/[.$]/);
          if (sessionParts.length >= 3) {
            const sessionId = sessionParts[2].replace(/^s/, '');
            if (sessionId) {
              trackingIds.google_session_id = sessionId;
              break; // Use the first matching session cookie
            }
          }
        } catch (error) {
          console.warn('Error parsing Google Analytics session ID:', error);
        }
      }
    }

    // Extract Facebook browser ID from _fbp cookie
    if (cookies._fbp) {
      trackingIds.facebook_fbp = cookies._fbp;
    }

    // Extract Facebook click ID from _fbc cookie
    if (cookies._fbc) {
      trackingIds.facebook_fbc = cookies._fbc;
    }

    return trackingIds;
  } catch (error) {
    console.warn('Error parsing tracking cookies:', error);
    return {};
  }
}

/**
 * Enhanced analytics record function that includes tracking identifiers
 */
function recordWithTracking(eventName: string, properties: any = {}) {
  const trackingIds = getTrackingIdentifiers('09N6VRHLQG');
  analytics.record(eventName, {
    ...properties,
    ...trackingIds,
  });
}
